// Lightweight helpers for Cloudflare Access JWT handling on the client/edge.
// NOTE: This does NOT verify signatures; do verification on the server.
export class CloudflareAccessValidationError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.code = code;
        this.name = "CloudflareAccessValidationError";
    }
}
function b64urlToString(b64url) {
    // Convert from URL-safe base64 to standard and fix padding
    let b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4;
    if (pad === 2)
        b64 += "==";
    else if (pad === 3)
        b64 += "=";
    // Browser path
    if (typeof globalThis.atob === "function") {
        const ascii = globalThis.atob(b64);
        const bytes = Uint8Array.from(ascii, (c) => c.charCodeAt(0));
        return new TextDecoder().decode(bytes);
    }
    // Node/Edge path (ESM-safe): Buffer is globally available in Node runtimes
    if (typeof Buffer !== "undefined") {
        return Buffer.from(b64, "base64").toString("utf-8");
    }
    throw new CloudflareAccessValidationError("No base64 decoder available in this environment", "DECODE_UNSUPPORTED");
}
export function decodeJwtPayload(token) {
    try {
        const [, payload] = token.split(".");
        if (!payload)
            return null;
        const json = b64urlToString(payload);
        return JSON.parse(json);
    }
    catch {
        return null;
    }
}
export function isExpired(claims, skewSeconds = 60) {
    if (!claims?.exp)
        return true;
    const now = Math.floor(Date.now() / 1000);
    return now >= claims.exp - skewSeconds;
}
// Server-side JWT verification utilities
/**
 * Extract Cloudflare Access JWT from request headers
 */
export function extractCloudflareAccessToken(headers) {
    // Case-insensitive lookup for Cloudflare Access header
    let token;
    for (const [k, v] of Object.entries(headers)) {
        if (k.toLowerCase() === "cf-access-jwt-assertion") {
            token = v;
            break;
        }
    }
    if (!token) {
        // Fallback: Bearer Authorization (optional)
        const auth = Object.entries(headers).find(([k]) => k.toLowerCase() === "authorization")?.[1];
        if (typeof auth === "string" && auth.toLowerCase().startsWith("bearer ")) {
            return auth.slice(7);
        }
    }
    if (Array.isArray(token))
        return token[0] || null;
    return token || null;
}
/**
 * Validate Cloudflare Access JWT token (server-side)
 * Note: This is a placeholder for actual JWT verification.
 * In a real implementation, you would fetch JWKS and verify the signature.
 */
export async function validateCloudflareAccessToken(token, options = {}) {
    try {
        // Decode the token payload
        const claims = decodeJwtPayload(token);
        if (!claims) {
            return {
                isValid: false,
                error: "Invalid JWT format",
            };
        }
        // Validate required claims
        if (!claims.sub || !claims.email || !claims.iss || !claims.aud) {
            return {
                isValid: false,
                error: "Missing required claims",
            };
        }
        // Validate expiration
        if (isExpired(claims, options.clockSkewSeconds || 60)) {
            return {
                isValid: false,
                error: "Token expired",
            };
        }
        // Validate audience
        if (options.audience) {
            const audiences = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
            const expectedAudiences = Array.isArray(options.audience)
                ? options.audience
                : [options.audience];
            const hasValidAudience = audiences.some((aud) => expectedAudiences.includes(aud));
            if (!hasValidAudience) {
                return {
                    isValid: false,
                    error: "Invalid audience",
                };
            }
        }
        // Validate issuer
        if (options.issuer && claims.iss !== options.issuer) {
            return {
                isValid: false,
                error: "Invalid issuer",
            };
        }
        // Validate required groups
        if (options.requiredGroups && options.requiredGroups.length > 0) {
            const userGroups = claims.groups || [];
            const hasRequiredGroups = options.requiredGroups.every((group) => userGroups.includes(group));
            if (!hasRequiredGroups) {
                return {
                    isValid: false,
                    error: "Missing required groups",
                };
            }
        }
        return {
            isValid: true,
            claims,
        };
    }
    catch (error) {
        return {
            isValid: false,
            error: error instanceof Error ? error.message : "Validation failed",
        };
    }
}
/**
 * Get user info from validated claims
 */
export function getUserFromClaims(claims) {
    return {
        id: claims.sub,
        email: claims.email,
        name: claims.name,
        groups: claims.groups || [],
    };
}
// Interfaces and types are already exported above
// Force inclusion of all exports in compilation
// This ensures TypeScript generates declarations for all exported symbols
// Reference all exported symbols to prevent tree-shaking
const _cloudflareAccessClaims = {
    sub: "",
    email: "",
    name: "",
    aud: "",
    iss: "",
    exp: 0,
    iat: 0,
    groups: [],
};
const _validationOptions = {
    jwksUrl: "",
    audience: "",
    issuer: "",
    clockSkewSeconds: 60,
    requiredGroups: [],
};
const _validationResult = {
    isValid: false,
    claims: _cloudflareAccessClaims,
    error: "",
};
const _cloudflareAccessValidationError = new CloudflareAccessValidationError("", "");
// Export a dummy object that references all symbols
export const __allExports = {
    CloudflareAccessClaims: _cloudflareAccessClaims,
    ValidationOptions: _validationOptions,
    ValidationResult: _validationResult,
    CloudflareAccessValidationError: _cloudflareAccessValidationError,
    validateCloudflareAccessToken,
    extractCloudflareAccessToken,
    getUserFromClaims,
};
const ROLE_PRECEDENCE = [
    "tenant_owner",
    "tenant_admin",
    "tenant_editor",
    "tenant_viewer",
];
export function getRolesFromClaims(claims) {
    const ns = claims["https://instalaw.app/claims/roles"]; // namespaced preferred
    if (Array.isArray(ns))
        return ns;
    const roles = claims.roles;
    if (Array.isArray(roles))
        return roles;
    return [];
}
export function getPersonasFromClaims(claims) {
    const ns = claims["https://instalaw.app/claims/personas"]; // namespaced preferred
    if (Array.isArray(ns))
        return ns;
    const personas = claims.personas;
    if (Array.isArray(personas))
        return personas;
    return [];
}
export function getTenantSlugFromClaims(claims) {
    const issType = claims["https://instalaw.app/claims/iss_type"] ?? claims.iss_type;
    if (issType !== "tenant")
        return null;
    const tenant = claims["https://instalaw.app/claims/tenant"] ?? claims.tenant;
    return typeof tenant === "string" ? tenant : null;
}
export function getEffectiveTenantRole(roles) {
    // Normalize roles like "role:tenant:admin" -> "tenant_admin"
    const norm = roles
        .map((r) => r.replace(/^role:/, "").replace(/:/g, "_"))
        .filter((r) => r.startsWith("tenant_"));
    for (const want of ROLE_PRECEDENCE) {
        if (norm.includes(want))
            return want;
    }
    return null;
}
export function hasAtLeastRole(roles, min) {
    const eff = getEffectiveTenantRole(roles);
    if (!eff)
        return false;
    const idxEff = ROLE_PRECEDENCE.indexOf(eff);
    const idxMin = ROLE_PRECEDENCE.indexOf(min);
    return idxEff <= idxMin;
}
export function canAccessTenant(claims, slug) {
    const issType = claims["https://instalaw.app/claims/iss_type"] ?? claims.iss_type;
    if (issType !== "tenant")
        return false;
    const tenant = getTenantSlugFromClaims(claims);
    return tenant === slug;
}
